import { ReactNode, useMemo, useState } from "react";
import {
  autoUpdate,
  offset,
  safePolygon,
  useClick,
  useDismiss,
  useFloating,
  useFocus,
  useHover,
  useInteractions,
  useRole
} from "@floating-ui/react";
import { IconDotsVertical, IconX } from "@tabler/icons-react";
import { classNames } from "@ct-react/core";
import { useMediaQueryDown } from "../../hooks/media-queries";
import SpriteIcon from "../medias/sprite-icon";
import "./sticked-contact.scss";

type ItemProps = {
  className?: string;
  placement?: "left" | "left-end";
  hash: string;
  isMobile: boolean
  children?: ReactNode;
};

const StickedItem = ({ className, placement = "left", hash, isMobile, children }: ItemProps) => {

  const [ open, setOpen ] = useState(false);

  const { context, refs, floatingStyles } = useFloating({
    open,
    onOpenChange: setOpen,
    strategy: "fixed",
    placement,
    whileElementsMounted: autoUpdate,
    middleware: [ offset(12) ]
  });

  const { getReferenceProps, getFloatingProps } = useInteractions([
    useClick(context, { enabled: isMobile, toggle: true }),
    useHover(context, { enabled: !isMobile, move: false, handleClose: safePolygon() }),
    useDismiss(context, { ancestorScroll: !isMobile }),
    useRole(context, { role: "tooltip" })
  ]);

  return (
    <>
      <span ref={refs.setReference}
            {...getReferenceProps()}
            className={classNames("item", { opened: open })}>
        <SpriteIcon hash={hash} bordered={true} />
      </span>
      {open &&
        <span ref={refs.setFloating}
          {...getFloatingProps()}
              className={classNames("item-content", placement, className)}
              style={{...floatingStyles}}>
          {children}
        </span>
      }
    </>);
}

const StickedContact = () => {

  const [ open, setOpen ] = useState<boolean>(false);

  const { context, refs, floatingStyles } = useFloating({
    open,
    onOpenChange: setOpen,
    strategy: "fixed",
    placement: "top",
    middleware: [ offset(12) ]
  });

  const { getReferenceProps, getFloatingProps } = useInteractions([
    useClick(context),
    useFocus(context),
    useDismiss(context, { ancestorScroll: true })
  ]);

  const isMobileOnly = useMediaQueryDown("mobile");
  const itemsRendering = useMemo(() => (
    <>
      <StickedItem hash="contact.phone" isMobile={isMobileOnly}>
        <a href="tel:+41791948392">+41 (0)79 194 83 92</a>
      </StickedItem>
      <StickedItem hash="social.whatsapp" isMobile={isMobileOnly}>
        <a href="https://call.whatsapp.com/voice/x4TOi7NqaSlVQy5aCtxmd5" target="_blank" rel="noopener">
          +41 (0)79 194 83 92
        </a>
      </StickedItem>
      <StickedItem hash="contact.email" isMobile={isMobileOnly}>
        <a href="mailto:info@evres.ch">info@evres.ch</a>
      </StickedItem>
      <StickedItem className="address"
                   placement="left-end"
                   hash="contact.pin-location"
                   isMobile={isMobileOnly}>
        <span>EV Real Estate & Services</span>
        <span>Chemin de Dzelon 19</span>
        <span>1997 Haute-Nendaz</span>
        <span>Valais, Suisse</span>
      </StickedItem>
    </>), [ isMobileOnly ]);

  if (!isMobileOnly)
    return (
      <div className="sticked-contact list">
        {itemsRendering}
      </div>);

  return (
    <>
      <div ref={refs.setReference}
           {...getReferenceProps()}
           className="sticked-contact action">
        {open ? <IconX /> : <IconDotsVertical />}
      </div>
      {open &&
        <div ref={refs.setFloating}
             {...getFloatingProps()}
             className="list"
             style={{...floatingStyles}}>
          {itemsRendering}
        </div>
      }
    </>);

};

export default StickedContact;
